import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { clsx } from 'clsx';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'destructive' | 'destructive-secondary' | 'ghost';
export type ButtonSize = 'small' | 'medium';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The visual style variant of the button
   * @default 'primary'
   */
  variant?: ButtonVariant;
  /**
   * The size of the button
   * @default 'medium'
   */
  size?: ButtonSize;
  /**
   * If true, the component will be rendered as a child element
   * and merge its props with the child
   */
  asChild?: boolean;
  /**
   * The content of the button
   */
  children: React.ReactNode;
}

// Arbor Design System button styles
const buttonStyles = {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px', // Consistent spacing between icon and text
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontWeight: '500',
    borderRadius: '99px', // Pill shape
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    outline: 'none',
  },
  variants: {
    primary: {
      backgroundColor: '#0e8a0e', // brand-600
      color: '#ffffff',
      ':hover': {
        backgroundColor: '#005700', // brand-800
      },
      ':active': {
        backgroundColor: '#024002', // brand-900
      },
      ':focus-visible': {
        outline: '3px solid #3cad51', // brand-500
        outlineOffset: '0px',
      },
      ':disabled': {
        backgroundColor: '#b3b3b3', // grey-400
        cursor: 'not-allowed',
      },
    },
    secondary: {
      backgroundColor: '#ffffff',
      color: '#2f2f2f', // grey-900
      border: '1px solid #d1d1d1', // grey-300
      ':hover': {
        backgroundColor: '#f8f8f8', // grey-050
      },
      ':active': {
        backgroundColor: '#efefef', // grey-100
      },
      ':focus-visible': {
        outline: '3px solid #3cad51', // brand-500
        outlineOffset: '0px',
      },
      ':disabled': {
        backgroundColor: '#f8f8f8',
        color: '#b3b3b3',
        cursor: 'not-allowed',
      },
    },
    tertiary: {
      backgroundColor: '#efefef', // grey-100
      color: '#2f2f2f', // grey-900
      ':hover': {
        backgroundColor: '#dfdfdf', // grey-200
      },
      ':active': {
        backgroundColor: '#d1d1d1', // grey-300
      },
      ':focus-visible': {
        outline: '3px solid #3cad51', // brand-500
        outlineOffset: '0px',
      },
      ':disabled': {
        backgroundColor: '#f8f8f8', // grey-050
        color: '#b3b3b3', // grey-400
        cursor: 'not-allowed',
      },
    },
    destructive: {
      backgroundColor: '#c93232', // destructive-500
      color: '#ffffff',
      ':hover': {
        backgroundColor: '#920a0a', // destructive-700
      },
      ':active': {
        backgroundColor: '#610202', // destructive-800
      },
      ':focus-visible': {
        outline: '3px solid #e86565', // destructive-300
        outlineOffset: '0px',
      },
      ':disabled': {
        backgroundColor: '#b3b3b3',
        cursor: 'not-allowed',
      },
    },
    'destructive-secondary': {
      backgroundColor: '#ffffff',
      color: '#a62323', // destructive-600
      border: '1px solid #c93232', // destructive-500
      ':hover': {
        backgroundColor: '#a62323', // destructive-600
        color: '#ffffff',
        border: '1px solid #a62323',
      },
      ':active': {
        backgroundColor: '#920a0a', // destructive-700
        color: '#ffffff',
        border: '1px solid #920a0a',
      },
      ':focus-visible': {
        outline: '3px solid #3cad51', // brand-500 (green focus ring)
        outlineOffset: '0px',
      },
      ':disabled': {
        backgroundColor: '#ffffff',
        color: '#b3b3b3',
        border: '1px solid #d1d1d1',
        cursor: 'not-allowed',
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: '#0b800b', // brand-700 for link style
      textDecoration: 'underline',
      ':hover': {
        color: '#005700', // brand-800
      },
      ':active': {
        color: '#024002', // brand-900
      },
      ':focus-visible': {
        outline: '3px solid #3cad51',
        outlineOffset: '2px',
      },
      ':disabled': {
        color: '#b3b3b3',
        cursor: 'not-allowed',
      },
    },
  },
  sizes: {
    small: {
      height: '32px',
      fontSize: '13px',
      padding: '8px 16px',
    },
    medium: {
      height: '36px',
      fontSize: '13px',
      padding: '8px 16px',
    },
  },
};

/**
 * Button component - Arbor Design System
 *
 * A flexible button component with pill-shaped design following Arbor's design system.
 * Supports primary (green), secondary (outlined), destructive (red), and ghost (link-style) variants.
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="medium">
 *   Save Changes
 * </Button>
 * ```
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'medium',
      asChild = false,
      className,
      disabled,
      style,
      children,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const [isHovered, setIsHovered] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    const variantStyles = buttonStyles.variants[variant];
    const sizeStyles = buttonStyles.sizes[size];

    const combinedStyle: React.CSSProperties = {
      ...buttonStyles.base,
      ...variantStyles,
      ...sizeStyles,
      ...(isHovered && !disabled && variantStyles[':hover']),
      ...(isFocused && !disabled && variantStyles[':focus-visible']),
      ...(disabled && variantStyles[':disabled']),
      ...style,
    };

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsHovered(true);
      onMouseEnter?.(e);
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsHovered(false);
      onMouseLeave?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    return (
      <Comp
        ref={ref}
        className={clsx('arbor-button', className)}
        style={combinedStyle}
        disabled={disabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

Button.displayName = 'Button';
